import {
  Anchor,
  Badge,
  Button,
  Checkbox,
  Combobox,
  createTheme,
  Drawer,
  Input,
  InputLabel,
  InputWrapper,
  MultiSelect,
  NumberInput,
  PasswordInput,
  Pill,
  Radio,
  Select,
  Table,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";

import breakpoints from "./breakpoints.json";
import classes from "./mantineTheme.module.css";

const defaultProps = {
  size: "md",
  fw: 400,
  fz: "md",
};

export const inputDefaultProps = {
  size: "sm",
} as const;

const FONT_SIZES = {
  xs: "0.625rem",
  sm: "0.75rem",
  md: "0.875rem",
  lg: "1rem",
  xl: "1.25rem",
};

export const mantineTheme = createTheme({
  primaryColor: "burgundy",
  primaryShade: 9,
  components: {
    Anchor: Anchor.extend({ defaultProps }),
    Badge: Badge.extend({ defaultProps: { ...defaultProps, fz: "sm" } }),
    Button: Button.extend({ defaultProps: { ...defaultProps, size: "sm" } }),
    Checkbox: Checkbox.extend({ defaultProps, styles: { label: { fontSize: FONT_SIZES.md } } }),
    Combobox: Combobox.extend({
      defaultProps,
      styles: {
        option: { fontSize: FONT_SIZES.md },
        options: { fontSize: FONT_SIZES.md },
      },
    }),
    DateInput: DateInput.extend({ styles: { input: { fontSize: FONT_SIZES.md } } }),
    Drawer: Drawer.extend({ styles: { title: { fontSize: FONT_SIZES.lg, fontWeight: 600 } } }),
    Input: Input.extend({
      defaultProps: inputDefaultProps,
      classNames: classes,
      styles: { input: { fontSize: FONT_SIZES.md } },
    }),
    InputLabel: InputLabel.extend({
      defaultProps: { ...defaultProps, fw: 500 },
      styles: { label: { fontSize: FONT_SIZES.md } },
    }),
    InputWrapper: InputWrapper.extend({ styles: { root: { fontSize: FONT_SIZES.md } } }),
    MultiSelect: MultiSelect.extend({
      defaultProps: inputDefaultProps,
      styles: { input: { fontSize: FONT_SIZES.md } },
    }),
    NumberInput: NumberInput.extend({ defaultProps: inputDefaultProps }),
    Pill: Pill.extend({ styles: { root: { fontSize: FONT_SIZES.md } } }),
    PasswordInput: PasswordInput.extend({ defaultProps: inputDefaultProps }),
    Radio: Radio.extend({ defaultProps }),
    Select: Select.extend({ defaultProps: inputDefaultProps }),
    Table: Table.extend({ defaultProps }),
    TableCell: Table.Td.extend({ defaultProps }),
    TableRow: Table.Tr.extend({ defaultProps }),
    TextInput: TextInput.extend({ defaultProps: inputDefaultProps }),
    UnstyledButton: UnstyledButton.extend({ defaultProps }),
  },
  fontSizes: {
    xs: "0.625rem",
    sm: "0.75rem",
    md: "0.875rem",
    lg: "1rem",
    xl: "1.25rem",
  },
  breakpoints,
  headings: {
    sizes: {
      h1: { fontSize: "1.5rem", fontWeight: "600", lineHeight: "2rem" },
      h2: { fontSize: "1.25rem", fontWeight: "600", lineHeight: "2rem" },
      h3: { fontSize: "1rem", fontWeight: "600", lineHeight: "1.5rem" },
      h4: { fontSize: "0.875rem", fontWeight: "600", lineHeight: "1.25rem" },
      h5: { fontSize: "0.75rem", fontWeight: "600", lineHeight: "1.25rem" },
      h6: { fontSize: "0.625rem", fontWeight: "600", lineHeight: "1rem" },
    },
  },
  colors: {
    burgundy: [
      "#F8F1F3",
      "#FFE0E6",
      "#DCBCC5",
      "#CEA1AE",
      "#AD747E",
      "#AA4D56",
      "#994750",
      "#86424C",
      "#723A45",
      "#512F39",
    ],
    cream: [
      "#FFFBF9",
      "#FDF7F3",
      "#F7F2ED",
      "#F1ECE8",
      "#E3DED9",
      "#BFBAB5",
      "#78736F",
      "#645F5B",
      "#45413D",
      "#24201D",
    ],
    charcoal: [
      "#43434D",
      "#3B3B44",
      "#33333B",
      "#2B2B32",
      "#232329",
      "#1B1B20",
      "#131317",
      "#0B0B0E",
      "#030305",
      "#000000",
    ],
  },
  spacing: {
    xs: "0.5rem",
    sm: "0.75rem",
    md: "1rem",
    ms: "1.25rem",
    lg: "1.5rem",
    xlg: "1.875rem",
    xl: "2rem",
  },
});
